import axios, {
  type AxiosResponse,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { appSettingsService } from './AppSettingsService';
import { SessionStorageService } from './SessionStorageService';

const getAccessToken = (): string | null => {
  try {
    return SessionStorageService.getToken();
  } catch {
    return null;
  }
};

enum StatusCode {
  Unauthorized = 401,
  Forbidden = 403,
  TooManyRequests = 429,
  InternalServerError = 500,
}

const getDefaultHeaders = async (): Promise<Record<string, string>> => {
  try {
    const settings = await appSettingsService.getSettings();
    return {
      'Accept': 'application/json',
      'Content-Type': 'application/json; charset=utf-8',
      'x-apikey': settings.Api.ApiKey,
    };
  } catch {
    return {
      'Accept': 'application/json',
      'Content-Type': 'application/json; charset=utf-8',
      'x-apikey': 'C2sotgOZS66i5C8Dli848jfL13Bap686WoKhHsARGdWfKT1f',
    };
  }
};

const PUBLIC_ENDPOINTS = [
  '/assets/',
  '/config.js',
  '/public/',
  'appsettings',
  'firebase-messaging-sw.js',
];

const isPublicEndpoint = (url: string): boolean => {
  if (!url) return false;
  const urlLower = url.toLowerCase();
  return PUBLIC_ENDPOINTS.some(endpoint => urlLower.includes(endpoint.toLowerCase()));
};

const injectToken = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
  try {
    // Get JWT token from session storage
    const token = getAccessToken();
    
    // TOKEN EXISTS: Add to Authorization header
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (!token) {
      // NO TOKEN: Check if this is a public endpoint
      const isPublic = isPublicEndpoint(config.url || '');
      
      if (isPublic) {
        console.log(`HTTP Request: Public endpoint [${config.url}], allowing without token`);
      } else {
        // NO TOKEN on protected endpoint: REJECT REQUEST (don't redirect - let ProtectedRoute handle it)
        console.warn(`HTTP Request: No JWT token for protected endpoint [${config.url}] - request rejected`);
        
        // Reject the request to prevent it from being sent
        // Let ProtectedRoute handle the authentication redirect
        return Promise.reject({
          message: 'No authentication token found',
          isAuthError: true,
          config
        });
      }
    }

    // Ensure x-apikey is always present from settings
    if (config.headers) {
      try {
        const settings = await appSettingsService.getSettings();
        config.headers['x-apikey'] = settings.Api.ApiKey;
      } catch (error) {
        // Fallback API key
        config.headers['x-apikey'] = 'C2sotgOZS66i5C8Dli848jfL13Bap686WoKhHsARGdWfKT1f';
      }
    }

    // If the request data is FormData, remove Content-Type header to let browser set it with boundary
    if (config.data instanceof FormData) {
      if (config.headers) {
        delete config.headers['Content-Type'];
      }
    }

    return config;
  } catch (error) {
    console.error('HTTP Request: Error injecting token', error);
    throw error;
  }
};

class HttpClient {
  private client!: AxiosInstance;
  protected baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.initializeClient(baseUrl);
  }

  private async initializeClient(baseUrl: string) {
    const headers = await getDefaultHeaders();

    const httpClient = axios.create({
      baseURL: baseUrl,
      headers,
      withCredentials: false,
      timeout: 30000,
    });

    httpClient.interceptors.request.use(injectToken, (error) => Promise.reject(error));

    httpClient.interceptors.response.use(
      (response) => response,
      (error) => {
        return this.handleError(error);
      },
    );

    this.client = httpClient;
  }

  request<T, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R> {
    return this.client.request(config);
  }

  get<T, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.client.get<T, R>(url, config);
  }

  post<T, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> {
    return this.client.post<T, R>(url, data, config);
  }

  put<T, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> {
    return this.client.put<T, R>(url, data, config);
  }

  patch<T, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> {
    return this.client.patch<T, R>(url, data, config);
  }

  delete<T, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.client.delete<T, R>(url, config);
  }

  postBlob<T>(url: string, data?: T): Promise<AxiosResponse<Blob>> {
    return this.client.post<T, AxiosResponse<Blob>>(url, data, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/octet-stream'
      }
    });
  }

  private async redirectToUnauthorized(): Promise<void> {
    try {
      // Clear session storage and redirect to login
      sessionStorage.clear();
      localStorage.removeItem('userEmail');
      localStorage.removeItem('isLoggedIn');
      
      // Redirect to login page instead of unauthorized page
      const settings = await appSettingsService.getSettings();
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `${settings.App.ClientUrl}/?returnUrl=${returnUrl}`;
    } catch (error) {
      // Fallback to current origin root
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `${window.location.origin}/?returnUrl=${returnUrl}`;
    }
  }

  // Handle global app errors
  // We can handle generic app errors depending on the status code
  private handleError(error: any) {
    const response = error?.response;

    // Handle CORS errors specifically
    if (!response && error?.code === 'ERR_NETWORK') {
      throw new Error('Network error');
    }
    switch (response?.status) {
      case StatusCode.InternalServerError: {
        // Handle InternalServerError - throw error instead of returning response
        const errorDetails = response?.data ? JSON.stringify(response.data) : 'Unknown error';
        throw new Error(`Internal Server Error: ${response?.statusText || 'Unknown error'} - ${errorDetails}`);
      }
      case StatusCode.Forbidden: {
        // Handle Forbidden
        this.redirectToUnauthorized();
        break;
      }
      case StatusCode.Unauthorized: {
        // Handle Unauthorized
        this.redirectToUnauthorized();
        break;
      }
      case StatusCode.TooManyRequests: {
        // Handle TooManyRequests
        throw new Error(`Too Many Requests: ${response?.statusText || 'Rate limit exceeded'}`);
      }
      default: {
        // For any other error status codes, throw an error
        if (response && response.status >= 400) {
          throw new Error(`HTTP Error ${response.status}: ${response.statusText || 'Unknown error'}`);
        }
        return response;
      }
    }

    return response;
  }
}

export default HttpClient;
